import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URLS } from '../config/api';
import { apiPost, apiDelete } from '../utils/apiUtils';
import './OpenWorldScenesCategory.css';

const OpenWorldScenesCategory = ({ novelId, novel, setNovel, refreshNovel, isProduction = false }) => {
  const navigate = useNavigate();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const PAGE_SIZE = 10;
  const [page, setPage] = useState(0);

  const scenes = novel?.open_world_interactive_scenes || [];

  const handleCreateScene = async (event) => {
    event.preventDefault();
    setError(null);
    setLoading(true);

    const formData = new FormData(event.target);
    const name = formData.get('name')?.trim();
    const tag = formData.get('tag')?.trim() || '';

    if (!name) {
      setError('Название сцены обязательно');
      setLoading(false);
      return;
    }

    try {
      await apiPost(
        isProduction ? API_URLS.PRODUCTION_OPEN_WORLD_SCENES(novelId) : API_URLS.BUILDING_OPEN_WORLD_SCENES(novelId),
        { name, tag },
        { headers: { 'Content-Type': 'application/json' } },
        true,
        'Сцена открытого мира создана успешно!'
      );
      setShowCreateForm(false);
      await refreshNovel();
    } catch (err) {
      // Error shown by toast
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteScene = async (sceneId) => {
    if (!window.confirm('Вы уверены, что хотите удалить эту сцену открытого мира?')) return;

    try {
      setLoading(true);
      await apiDelete(
        isProduction ? API_URLS.PRODUCTION_OPEN_WORLD_SCENE(novelId, sceneId) : API_URLS.BUILDING_OPEN_WORLD_SCENE(novelId, sceneId),
        {},
        true,
        'Сцена открытого мира удалена успешно!'
      );
      await refreshNovel();
    } catch (err) {
      // Error shown by toast
    } finally {
      setLoading(false);
    }
  };

  const handleEditScene = (sceneId) => {
    const basePath = isProduction ? 'production_change' : 'building';
    navigate(`/${basePath}/${novelId}/open_world_scene/${sceneId}`);
  };

  const allTags = [...new Set(scenes
    .filter(scene => scene && scene.tag)
    .flatMap(scene => scene.tag.split(/\s+/).filter(tag => tag.trim()))
  )];

  const filtered = scenes.filter(scene => {
    const matchesSearch = scene && scene.name && scene.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTags = selectedTags.length === 0 ||
      (scene.tag && selectedTags.every(selectedTag =>
        scene.tag.split(/\s+/).includes(selectedTag)
      ));
    return matchesSearch && matchesTags;
  });

  useEffect(() => { setPage(0); }, [searchTerm, selectedTags]);
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <div className="owsc-wrap">
      <div className="owsc-desc">
        <p>Сцены открытого мира — интерактивные сцены с кастомными персонажами и фонами для открытого мира.</p>
      </div>

      {error && <div className="owsc-error">{error}</div>}

      {showCreateForm && (
        <div className="owsc-overlay">
          <div className="owsc-modal">
            <h3 className="owsc-modal-title">Добавить сцену открытого мира</h3>
            <form onSubmit={handleCreateScene}>
              <div className="owsc-form-group">
                <input type="text" name="name" placeholder="Название сцены" required disabled={loading} className="owsc-input" autoFocus />
              </div>
              <div className="owsc-form-group">
                <input type="text" name="tag" placeholder="Тег (необязательно)" disabled={loading} className="owsc-input" />
              </div>
              <div className="owsc-modal-actions">
                <button type="submit" disabled={loading} className="owsc-btn-submit">
                  {loading ? 'Загрузка...' : 'Создать'}
                </button>
                <button type="button" onClick={() => { setShowCreateForm(false); setError(null); }} disabled={loading} className="owsc-btn-cancel">
                  Отменить
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {allTags.length > 0 && (
        <div className="owsc-tags">
          <button className={`owsc-tag ${selectedTags.length === 0 ? 'active' : ''}`} onClick={() => setSelectedTags([])}>Все</button>
          {allTags.map(tag => (
            <button
              key={tag}
              className={`owsc-tag ${selectedTags.includes(tag) ? 'active' : ''}`}
              onClick={() => {
                if (selectedTags.includes(tag)) {
                  setSelectedTags(selectedTags.filter(t => t !== tag));
                } else {
                  setSelectedTags([...selectedTags, tag]);
                }
              }}
            >
              {tag}
            </button>
          ))}
        </div>
      )}

      <div className="owsc-toolbar">
        <input type="text" placeholder="Поиск сцен..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="owsc-search" />
        {(searchTerm || selectedTags.length > 0) && (
          <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', fontFamily: "'Raleway', sans-serif" }}>
            {filtered.length} сцен найдено
          </span>
        )}
        <button className="owsc-add-btn" onClick={() => { setShowCreateForm(true); setError(null); }} disabled={loading}>
          + Добавить сцену открытого мира
        </button>
      </div>

      <div className="owsc-list">
        {paged.length > 0 ? (
          paged.map((scene) => (
            <div key={scene.id} className="owsc-card">
              <div className="owsc-card-head">
                <h4 className="owsc-card-name">{scene.name}</h4>
                <div className="owsc-card-actions">
                  {scene.tag && <span className="owsc-card-tag">{scene.tag}</span>}
                  <button className="owsc-btn-edit" onClick={() => handleEditScene(scene.id)} disabled={loading}>Редактировать</button>
                  <button className="owsc-btn-delete" onClick={() => handleDeleteScene(scene.id)} disabled={loading}>Удалить</button>
                </div>
              </div>
            </div>
          ))
        ) : searchTerm ? (
          <div className="owsc-empty"><p>Сцены не найдены по запросу "{searchTerm}"</p></div>
        ) : (
          <div className="owsc-empty"><p>Сцен открытого мира пока нет. Создайте свою первую сцену!</p></div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="owsc-pagination">
          <button className="owsc-page-btn" onClick={() => setPage(p => p - 1)} disabled={page === 0}>← Назад</button>
          <span>Страница {page + 1} из {totalPages}</span>
          <button className="owsc-page-btn" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}>Вперёд →</button>
        </div>
      )}
    </div>
  );
};

export default OpenWorldScenesCategory;
